import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import MobileStickyBar from "@/components/layout/MobileStickyBar";
import CookieConsent from "@/components/layout/CookieConsent";
import WhatsAppFab from "@/components/layout/WhatsAppFab";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Capture the site-wide default meta values declared in public/index.html the
// first time this module is imported. We use these as fallbacks when a page
// does not supply a per-page override, so previously-set page-specific values
// (e.g. a service hero image) never leak into the next route during SPA
// navigation. Captured once at module load — before any PageLayout mounts and
// overrides them — so defaults remain stable for the lifetime of the SPA.
const SITE_META_DEFAULTS = (() => {
  if (typeof document === "undefined") return {};
  const readProperty = (p) => document.querySelector(`meta[property="${p}"]`)?.getAttribute("content") || null;
  const readName = (n) => document.querySelector(`meta[name="${n}"]`)?.getAttribute("content") || null;
  return {
    og: {
      "og:title": readProperty("og:title"),
      "og:description": readProperty("og:description"),
      "og:url": readProperty("og:url"),
      "og:image": readProperty("og:image"),
      "og:type": readProperty("og:type"),
    },
    name: {
      description: readName("description"),
      "twitter:card": readName("twitter:card"),
      "twitter:title": readName("twitter:title"),
      "twitter:description": readName("twitter:description"),
      "twitter:image": readName("twitter:image"),
    },
  };
})();

export default function PageLayout({
  title,
  description,
  canonical,
  titleSuffix = " · Clinica Denti Service Bacău",
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  ogType,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  children,
}) {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (title) document.title = `${title}${titleSuffix}`;
    // meta description: override when the page provides one, otherwise
    // restore the site-wide default captured from index.html so a previous
    // route's description does not leak into the next SPA route.
    const descContent = description || SITE_META_DEFAULTS.name.description;
    if (descContent) {
      let el = document.querySelector('meta[name="description"]');
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", "description");
        document.head.appendChild(el);
      }
      el.setAttribute("content", descContent);
    }
  }, [title, description, titleSuffix]);

  // Per-page Open Graph + Twitter overrides with SPA-safe fallbacks. Every
  // managed tag is written on each pathname change: when the page supplies an
  // override we use it, otherwise we restore the site-wide default captured
  // from public/index.html at module load. This prevents stale metadata from
  // the previous route (e.g. a service-specific og:image) from persisting
  // when navigating to a page that does not override that tag.
  useEffect(() => {
    const upsert = (selector, attrName, attrValue, content) => {
      let el = document.querySelector(selector);
      if (content == null) {
        // No override and no captured default — remove any stale tag so we
        // never emit a value that misrepresents the current page.
        if (el) el.remove();
        return;
      }
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const writeMeta = (property, override) => {
      const value = override || SITE_META_DEFAULTS.og[property] || null;
      upsert(`meta[property="${property}"]`, "property", property, value);
    };
    const writeNameMeta = (name, override) => {
      const value = override || SITE_META_DEFAULTS.name[name] || null;
      upsert(`meta[name="${name}"]`, "name", name, value);
    };
    // og:url resolution — priority: explicit prop > canonical > current route
    const normalizedPath = pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
    const resolvedOgUrl = ogUrl || canonical || `https://www.dentiservice.ro${normalizedPath}`;

    writeMeta("og:title", ogTitle);
    writeMeta("og:description", ogDescription);
    writeMeta("og:url", resolvedOgUrl);
    writeMeta("og:image", ogImage);
    writeMeta("og:type", ogType);
    // Keep og:image:secure_url in lockstep with og:image so Facebook / older
    // crawlers that prefer the secure_url form never read a different (stale)
    // image than the primary og:image tag. Omitted entirely when no image is
    // available (page override absent AND no site default).
    const resolvedImage = ogImage || SITE_META_DEFAULTS.og["og:image"] || null;
    upsert('meta[property="og:image:secure_url"]', "property", "og:image:secure_url", resolvedImage);
    // Twitter: fall back to OG twins, then to captured site defaults so
    // nothing persists across routes.
    writeNameMeta("twitter:card", twitterCard);
    writeNameMeta("twitter:title", twitterTitle || ogTitle);
    writeNameMeta("twitter:description", twitterDescription || ogDescription);
    writeNameMeta("twitter:image", twitterImage || ogImage);
  }, [ogTitle, ogDescription, ogUrl, ogImage, ogType, twitterCard, twitterTitle, twitterDescription, twitterImage, canonical, pathname]);

  // Per-page canonical: if the page supplies a specific canonical URL (e.g. a
  // legacy WordPress URL that Google Search Console has as canonical), write
  // that. Otherwise reflect the current route on the canonical www origin so
  // paginated / query-string variations do not create duplicates in the index.
  //
  // Fallback branch normalises a stray trailing slash so that visiting
  // /echipa-medicala/ still emits the sitemap-declared canonical
  // /echipa-medicala (no slash). Homepage "/" is preserved. Pages that pass
  // an explicit `canonical` prop are untouched — e.g. AllOn4 deliberately
  // keeps its trailing-slash canonical.
  useEffect(() => {
    let el = document.querySelector('link[rel="canonical"]');
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", "canonical");
      document.head.appendChild(el);
    }
    const normalizedPath = pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
    const href = canonical || `https://www.dentiservice.ro${normalizedPath}`;
    el.setAttribute("href", href);
  }, [canonical, pathname]);

  useEffect(() => {
    if (hash) {
      const target = hash.slice(1);
      let attempts = 0;
      const tick = () => {
        const el = document.getElementById(target);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        if (attempts++ < 10) setTimeout(tick, 100);
      };
      setTimeout(tick, 60);
    } else if (window.__implantShell) {
      // First load over the early-HTML shell: keep the visitor's scroll position.
      window.__implantShell = false;
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return (
    <>
      <Header />
      <main className="pb-24 lg:pb-0">{children}</main>
      <Footer />
      <MobileStickyBar />
      <WhatsAppFab />
      <CookieConsent />
    </>
  );
}
